import React from 'react';
import { formatCatalogPrice, getCachedDisplayRates } from '../../lib/displayCurrency';

// Compact product card attached to a chat message (messages.product_ref), and
// to the pending attachment above a composer.
//
// Lifted out of ThreadChat, which had it as a private component styled by the
// `.ftc-pref*` rules in its own injected <style> block. Chat.jsx needs the same
// card for direct buyer<->supplier conversations and does not inject that CSS,
// so the styling is inline and self-contained here. The one thing the cascade
// used to do for free was the light-on-dark variant inside the sender's own
// bubble (`.ftc-row.me .ftc-pref`); that is now the explicit `onDark` prop.
//
// Self-contained from the stored ref: it renders whatever was written at send
// time and never re-reads the product, so a later price or name change does not
// rewrite history.
export default function ProductRefCard({ pr, lang = 'ar', link = true, onDark = false, style, viewerIsSupplier = false }) {
  if (!pr) return null;
  const isAr = lang === 'ar';
  const name = (isAr ? pr.name_ar : lang === 'zh' ? pr.name_zh : pr.name_en)
    || pr.name_en || pr.name_ar || pr.name_zh || (isAr ? 'منتج' : lang === 'zh' ? '产品' : 'Product');

  // Factory catalog rows carry `image`; supplier rows carry `image_url`.
  const image = pr.image || pr.image_url || null;

  // A trader sees SAR only (owner, 2026-09-29), through the catalogue's own
  // formatter at the admin rate (cached by App's fetchDisplayRates) — so a
  // CNY price reads "On request" here too. The supplier side keeps the
  // stored price and currency. SAR is the default: a caller that forgets
  // the flag shows SAR, never a foreign figure.
  const price = (pr.price || '').toString().trim();
  const supplierTxt = price
    ? (pr.currency && !price.toLowerCase().includes(String(pr.currency).toLowerCase()) ? `${price} ${pr.currency}` : price)
    : null;
  const sarTxt = price ? formatCatalogPrice({ price, currency: pr.currency }, isAr, getCachedDisplayRates()) : null;
  const priceTxt = (viewerIsSupplier ? supplierTxt : sarTxt)
    || (isAr ? 'السعر عند الطلب' : lang === 'zh' ? '价格面议' : 'On request');

  const wrap = {
    display: 'flex', gap: 10, alignItems: 'center', textDecoration: 'none',
    background: onDark ? 'rgba(255,255,255,0.12)' : '#fff',
    border: `1px solid ${onDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.1)'}`,
    borderRadius: 12, padding: 8, maxWidth: 300, marginBottom: 4, ...style,
  };
  const imgStyle = { width: 46, height: 46, borderRadius: 8, objectFit: 'cover', background: '#efe9df', flexShrink: 0 };
  const nameStyle = {
    fontSize: 13, fontWeight: 600, color: onDark ? '#fff' : 'rgba(0,0,0,0.85)',
    fontFamily: 'var(--font-sans)', lineHeight: 1.35,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  };
  const subStyle = {
    fontSize: 11.5, color: onDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.5)',
    fontFamily: 'var(--font-sans)', marginTop: 2,
  };

  const inner = (
    <>
      {image ? <img style={imgStyle} src={image} alt="" /> : <div style={imgStyle} />}
      <div style={{ minWidth: 0 }}>
        <div style={nameStyle}>{name}</div>
        <div style={subStyle}>{pr.ref_code ? `${isAr ? 'رمز' : 'Ref'}: ${pr.ref_code} · ` : ''}{priceTxt}</div>
      </div>
    </>
  );

  // A ref from the factory catalog points at the factory product page; one from
  // a self-registered supplier has no factory_id and points at /products/:id;
  // a «حصريات معبر» ref (kind 'exclusive') at the store's exclusives page. The
  // path is built here from the id, never taken from the stored ref.
  if (link && pr.id) {
    const href = pr.kind === 'exclusive' ? `/market/exclusives/${encodeURIComponent(pr.id)}`
      : pr.factory_id ? `/factory/${pr.factory_id}/product/${pr.id}` : `/products/${pr.id}`;
    return <a style={wrap} href={href} target="_blank" rel="noreferrer">{inner}</a>;
  }
  return <div style={wrap}>{inner}</div>;
}
